<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Toaster } from "@/components/ui/sonner";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { SIDEBAR_COOKIE_NAME } from "@/components/ui/sidebar/utils";
import { Separator } from "@/components/ui/separator";
import { Zap } from "@lucide/vue";
import { Spinner } from "@/components/ui/spinner";
import AppSidebar from "@/components/AppSidebar.vue";
import ThemeToggle from "@/components/ThemeToggle.vue";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Hold the splash until the FIRST navigation fully settles. isReady() resolves
// after the initial route — including the guard's auth-rehydrate and any redirect
// to /login — has committed, so the app chrome never paints for a route we're
// about to be redirected away from.
const ready = ref(false);
router.isReady().finally(() => {
  ready.value = true;
});

// First visit below 1024px starts with the icon rail so the planning grids get
// the width; a saved toggle (cookie) always wins.
const sidebarDefaultOpen = document.cookie.includes(`${SIDEBAR_COOKIE_NAME}=`)
  ? !document.cookie.includes(`${SIDEBAR_COOKIE_NAME}=false`)
  : window.innerWidth >= 1024;

const shouldHideSidebar = computed(() => !!route.meta.hideSidebar);
// Some views (the workout editor) render their own full header — suppress the
// generic app-chrome header there so the title isn't shown twice.
const shouldHideHeader = computed(() => !!route.meta.hideHeader);
const pageTitle = computed(() => {
  const key = route.meta.title as string | undefined;
  return key ? t(key) : t("routeTitles.zenith");
});
</script>

<template>
  <Toaster richColors />

  <!-- Boot splash: held until the first navigation settles (auth rehydrate +
       any login redirect committed), so the app chrome never flashes before a
       redirect to login. -->
  <div
    v-if="!ready"
    class="flex min-h-svh flex-col items-center justify-center gap-5 bg-background"
  >
    <div
      class="flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/25"
    >
      <Zap class="size-6" />
    </div>
    <Spinner class="size-5 text-muted-foreground" aria-hidden="true" />
    <span class="sr-only">{{ t("common.loading") }}</span>
  </div>

  <template v-else>
    <RouterView v-if="shouldHideSidebar" v-slot="{ Component, route: r }">
      <Transition name="route-fade" mode="out-in">
        <component :is="Component" :key="r.name" />
      </Transition>
    </RouterView>
    <SidebarProvider v-else :default-open="sidebarDefaultOpen">
      <AppSidebar />
      <SidebarInset class="min-w-0">
        <header
          v-if="!shouldHideHeader"
          class="flex h-16 shrink-0 items-center gap-2 border-b px-4"
        >
          <SidebarTrigger class="-ml-1" />
          <Separator orientation="vertical" class="mr-1 data-[orientation=vertical]:h-4" />
          <span class="text-sm font-medium">{{ pageTitle }}</span>
          <div class="flex-1" />
          <ThemeToggle />
        </header>
        <RouterView v-slot="{ Component, route: r }">
          <Transition name="route-fade" mode="out-in">
            <component :is="Component" :key="r.name" />
          </Transition>
        </RouterView>
      </SidebarInset>
    </SidebarProvider>
  </template>
</template>

<style>
/* Enter-only fade: the outgoing page leaves instantly, the incoming one eases in. */
.route-fade-enter-active {
  transition: opacity var(--dur-fast) var(--ease-out);
}
.route-fade-enter-from {
  opacity: 0;
}
</style>
